class AxisrayNewsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.viewport = this.querySelector('[data-axn-viewport]');
    this.track = this.querySelector('.axn-home__track');
    this.cards = Array.from(this.querySelectorAll('[data-axn-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-axn-dot]'));
    this.previousButton = this.querySelector('[data-axn-previous]');
    this.nextButton = this.querySelector('[data-axn-next]');
    this.steps = [];
    this.activeStep = 0;
    this.requestedStep = 0;
    this.programmaticScroll = false;
    this.scrollFrame = null;
    this.scrollEndTimer = null;

    if (!this.viewport || this.cards.length < 1) return;

    this.previousButton?.addEventListener('click', () => this.goToStep(this.requestedStep - 1));
    this.nextButton?.addEventListener('click', () => this.goToStep(this.requestedStep + 1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goToStep(Number(dot.dataset.axnStep || 0)));
    });

    this.viewport.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.viewport.addEventListener('scrollend', () => this.settleScroll(), { passive: true });
    this.viewport.addEventListener('pointerdown', () => this.beginManualScroll(), { passive: true });
    this.viewport.addEventListener('touchstart', () => this.beginManualScroll(), { passive: true });
    this.viewport.addEventListener('wheel', () => this.beginManualScroll(), { passive: true });
    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goToStep(this.requestedStep - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goToStep(this.requestedStep + 1);
      }
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.refreshSteps());
      this.resizeObserver.observe(this.viewport);
      if (this.track) this.resizeObserver.observe(this.track);
    } else {
      this.handleResize = () => this.refreshSteps();
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    requestAnimationFrame(() => this.refreshSteps());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
  }

  refreshSteps() {
    const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const firstOffset = this.cards[0]?.offsetLeft || 0;
    const nextSteps = [];

    this.cards.forEach((card, cardIndex) => {
      const left = Math.max(0, Math.min(card.offsetLeft - firstOffset, maxScroll));
      const previous = nextSteps[nextSteps.length - 1];

      if (previous && Math.abs(previous.left - left) < 2) {
        // Multiple articles can share the final reachable scroll position.
        // Keep one physical page instead of exposing a no-op arrow step.
        previous.cardIndex = cardIndex;
      } else {
        nextSteps.push({ left, cardIndex });
      }
    });

    this.steps = nextSteps.length ? nextSteps : [{ left: 0, cardIndex: 0 }];
    const nearest = this.getNearestStep(this.viewport.scrollLeft);
    this.activeStep = nearest;
    this.requestedStep = nearest;
    this.updateControls();
  }

  getNearestStep(scrollLeft) {
    let nearestStep = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.steps.forEach((step, index) => {
      const distance = Math.abs(step.left - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestStep = index;
      }
    });

    return nearestStep;
  }

  beginManualScroll() {
    this.programmaticScroll = false;
    const nearest = this.getNearestStep(this.viewport.scrollLeft);
    this.activeStep = nearest;
    this.requestedStep = nearest;
    this.updateControls();
  }

  handleScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const nearest = this.getNearestStep(this.viewport.scrollLeft);
      this.activeStep = nearest;
      if (!this.programmaticScroll) this.requestedStep = nearest;
      this.updateControls();
      this.scheduleSettle();
    });
  }

  scheduleSettle() {
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => this.settleScroll(), 160);
  }

  settleScroll() {
    if (!this.steps.length) return;
    this.programmaticScroll = false;
    const nearest = this.getNearestStep(this.viewport.scrollLeft);
    this.activeStep = nearest;
    this.requestedStep = nearest;
    this.updateControls();
  }

  goToStep(index) {
    if (!this.steps.length) this.refreshSteps();
    const nextStep = Math.max(0, Math.min(index, this.steps.length - 1));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.requestedStep = nextStep;
    this.activeStep = nextStep;
    this.programmaticScroll = true;
    this.updateControls();
    this.viewport.scrollTo({
      left: this.steps[nextStep].left,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
    this.scheduleSettle();
  }

  updateControls() {
    this.dots.forEach((dot, index) => {
      const visible = index < this.steps.length;
      dot.hidden = !visible;
      dot.dataset.axnStep = String(index);
      dot.setAttribute('aria-label', `Show news page ${index + 1}`);
      dot.setAttribute('aria-current', visible && index === this.requestedStep ? 'true' : 'false');
    });

    if (this.previousButton) this.previousButton.disabled = this.requestedStep <= 0;
    if (this.nextButton) this.nextButton.disabled = this.requestedStep >= this.steps.length - 1;
  }
}

if (!customElements.get('axisray-news-carousel')) {
  customElements.define('axisray-news-carousel', AxisrayNewsCarousel);
}
